import React from 'react'
import styled from 'styled-components'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import TabletMacIcon from '@material-ui/icons/TabletMac'
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo'
import PeopleIcon from '@material-ui/icons/People'

export default function TutorSelectionTimeline() {
    const steps = [
        {
            icon: <VerifiedUserIcon />,
            title: 'Profile Screening',
            description: 'We verify educational qualifications, teaching experience, and subject expertise.',
            color: '#3498db'
        },
        {
            icon: <TabletMacIcon />,
            title: 'Gadget Requirements',
            description: 'All tutors use Digiboards or Pen Tabs for core subjects like Math, Science, and English, and at least a whiteboard for other subjects to ensure clear and engaging lessons.',
            color: '#9b59b6'
        },
        {
            icon: <OndemandVideoIcon />,
            title: 'Demo Class Evaluation',
            description: 'Tutors conduct a live demo session to assess communication skills, concept clarity, and student engagement.',
            color: '#e74c3c'
        },
        {
            icon: <PeopleIcon />,
            title: 'Personality Review',
            description: 'Our academic panel evaluates teaching methods, patience, and emotional connection with students.',
            color: '#27ae60'
        }
    ]

    return (
        <Section>
            <Container>
                <SectionHeader>
                    <Badge>Quality Assurance</Badge>
                    <Title>Our Tutor Selection Process</Title>
                    <Subtitle>
                        At teacherInd, we follow a rigorous multi-step interview and screening process 
                        to ensure only the most qualified and passionate tutors join our platform.
                    </Subtitle>
                </SectionHeader>

                <TimelineContainer>
                    {steps.map((step, index) => (
                        <TimelineItem key={index} isLast={index === steps.length - 1}>
                            <TimelineLine color={step.color} />
                            <TimelineIcon color={step.color}>
                                {step.icon}
                                <StepNumber>{index + 1}</StepNumber>
                            </TimelineIcon>
                            <TimelineContent>
                                <TimelineTitle>{step.title}</TimelineTitle>
                                <TimelineDescription>{step.description}</TimelineDescription>
                            </TimelineContent>
                        </TimelineItem>
                    ))}
                </TimelineContainer>

                <StatsSection>
                    <StatBox>
                        <StatNumber>1 in 8</StatNumber>
                        <StatLabel>Acceptance Rate</StatLabel>
                    </StatBox>
                    <StatBox>
                        <StatNumber>4 Steps</StatNumber>
                        <StatLabel>Rigorous Screening</StatLabel>
                    </StatBox>
                    <StatBox>
                        <StatNumber>100%</StatNumber>
                        <StatLabel>Qualified Tutors</StatLabel>
                    </StatBox>
                </StatsSection>

                <CTASection>
                    <CTATitle>Ready to learn from the best?</CTATitle>
                    <CTAButton href="/form">Book a Free Class</CTAButton>
                </CTASection>
            </Container>
        </Section>
    )
}

const Section = styled.section`
    padding: 80px 20px;
    background: #ffffff;
    position: relative;
`

const Container = styled.div`
    max-width: 900px;
    margin: 0 auto;
`

const SectionHeader = styled.div`
    text-align: center;
    margin-bottom: 60px;
`

const Badge = styled.span`
    display: inline-block;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 8px 20px;
    border-radius: 50px;
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 20px;
`

const Title = styled.h2`
    font-size: 2.8rem;
    color: #2c3e50;
    font-weight: 700;
    margin-bottom: 20px;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`

const Subtitle = styled.p`
    font-size: 1.1rem;
    color: #7f8c8d;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.8;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`

const TimelineContainer = styled.div`
    position: relative;
    padding: 40px 0;
`

const TimelineItem = styled.div`
    position: relative;
    display: flex;
    margin-bottom: ${props => props.isLast ? '0' : '60px'};
    padding-left: 120px;

    @media (max-width: 768px) {
        padding-left: 80px;
        margin-bottom: ${props => props.isLast ? '0' : '40px'};
    }
`

const TimelineLine = styled.div`
    position: absolute;
    left: 44px;
    top: 80px;
    bottom: -60px;
    width: 3px;
    background: linear-gradient(to bottom, ${props => props.color}, transparent);

    @media (max-width: 768px) {
        left: 29px;
    }
`

const TimelineIcon = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: ${props => props.color};
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 20px ${props => props.color}40;
    z-index: 2;

    svg {
        font-size: 2.5rem;
        color: white;
    }

    @media (max-width: 768px) {
        width: 60px;
        height: 60px;

        svg {
            font-size: 1.8rem;
        }
    }
`

const StepNumber = styled.div`
    position: absolute;
    top: -10px;
    right: -10px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: white;
    color: #2c3e50;
    font-weight: 700;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
        width: 25px;
        height: 25px;
        font-size: 0.8rem;
        top: -8px;
        right: -8px;
    }
`

const TimelineContent = styled.div`
    background: white;
    border-radius: 15px;
    padding: 30px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
    flex: 1;
    transition: all 0.3s ease;
    border-left: 4px solid transparent;

    &:hover {
        transform: translateX(10px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
        border-left-color: #667eea;
    }

    @media (max-width: 768px) {
        padding: 20px;
    }
`

const TimelineTitle = styled.h3`
    font-size: 1.5rem;
    color: #2c3e50;
    font-weight: 700;
    margin-bottom: 15px;

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`

const TimelineDescription = styled.p`
    font-size: 1rem;
    color: #7f8c8d;
    line-height: 1.7;

    @media (max-width: 768px) {
        font-size: 0.95rem;
    }
`

const StatsSection = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    margin: 60px 0;
    padding: 40px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 20px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding: 30px 20px;
    }
`

const StatBox = styled.div`
    text-align: center;
`

const StatNumber = styled.div`
    font-size: 2.5rem;
    font-weight: 800;
    color: white;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`

const StatLabel = styled.div`
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;

    @media (max-width: 768px) {
        font-size: 0.9rem;
    }
`

const CTASection = styled.div`
    text-align: center;
    padding: 40px;
    background: #f8f9fa;
    border-radius: 20px;
`

const CTATitle = styled.h3`
    font-size: 1.8rem;
    color: #2c3e50;
    font-weight: 700;
    margin-bottom: 25px;

    @media (max-width: 768px) {
        font-size: 1.4rem;
    }
`

const CTAButton = styled.a`
    display: inline-block;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 15px 40px;
    border-radius: 50px;
    font-size: 1.1rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }

    @media (max-width: 768px) {
        padding: 12px 30px;
        font-size: 1rem;
    }
`